<script lang="ts" setup>
import { isMobile } from 'mobile-device-detect';
import { computed, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import { AggregationOperation } from '@exchange/libs/orderbook/service/src';

enum HoverType {
  hoverPlus = 'hoverPlus',
  hoverMinus = 'hoverMinus',
}

const props = defineProps({
  reducePossible: {
    type: Boolean,
    required: true,
  },
  raisePossible: {
    type: Boolean,
    required: true,
  },
  level: {
    type: [Number, String],
    required: true,
  },
  mini: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits<{
  (e: 'aggregate', v: AggregationOperation): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const hoverPlus = ref(false);
const hoverMinus = ref(false);

const toggle = (hoverType: HoverType) => {
  if (!isMobile) {
    return;
  }

  switch (hoverType) {
    case HoverType.hoverPlus:
      hoverPlus.value = !hoverPlus.value;
      break;
    case HoverType.hoverMinus:
      hoverMinus.value = !hoverMinus.value;
      break;
    default:
      break;
  }
};

const aggregationValue = computed(() => {
  if (Number.isNaN(Number(props.level))) {
    return t('modules.orderbook.none');
  }

  return props.level;
});
const hoverPlusTimeout = ref();
const hoverMinusTimeout = ref();

const reduceAggregation = () => {
  if (props.reducePossible) {
    toggle(HoverType.hoverMinus);
    emit('aggregate', AggregationOperation.REDUCE);

    hoverMinusTimeout.value = setTimeout(() => toggle(HoverType.hoverMinus), 500);
  }
};
const raiseAggregation = () => {
  if (props.raisePossible) {
    toggle(HoverType.hoverPlus);
    emit('aggregate', AggregationOperation.RAISE);

    hoverPlusTimeout.value = setTimeout(() => toggle(HoverType.hoverPlus), 500);
  }
};

onBeforeUnmount(() => {
  if (hoverPlusTimeout.value) {
    clearTimeout(hoverPlusTimeout.value);
  }

  if (hoverMinusTimeout.value) {
    clearTimeout(hoverMinusTimeout.value);
  }
});
</script>

<template>
  <div class="orderbook-aggregation">
    <div
      class="orderbook-aggregation__button"
      :class="{ 'orderbook-aggregation__button--disabled': !raisePossible }"
      @click.stop="raiseAggregation()"
    >
      <ot-icon name="add-square" />
    </div>
    <span
      class="orderbook-aggregation__value"
      :class="{ 'orderbook-aggregation__value--mini': mini }"
      >{{ aggregationValue }}</span
    >
    <div
      class="orderbook-aggregation__button"
      :class="{ 'orderbook-aggregation__button--disabled': !reducePossible }"
      @click.stop="reduceAggregation()"
    >
      <ot-icon name="minus-square" />
    </div>
  </div>
</template>

<style lang="scss">
/* stylelint-disable no-descending-specificity */

/** TODO fix styles and enable stylelint */
.orderbook-aggregation {
  display: flex;
  gap: 5px;

  &__value {
    --ag-value-width: 60px;

    width: var(--ag-value-width);
    color: rgb(var(--v-theme-text-tertiary));
    text-align: center;

    &--mini {
      --ag-value-width: 40px;
    }
  }

  &__button {
    --icon-bg-color: transparent;
    --icon-stroke-color: rgb(var(--v-theme-text-tertiary));

    width: 14px;
    height: 14px;
    cursor: pointer;

    .svg-icon {
      &__bg {
        fill: var(--icon-bg-color);
        transition: 0.2s;
      }

      &__content {
        stroke: var(--icon-stroke-color);
      }

      &__border {
        stroke: rgb(var(--v-theme-text-tertiary));
      }
    }

    &:hover {
      --icon-bg-color: rgb(var(--v-theme-text-tertiary));
      --icon-stroke-color: rgb(var(--v-theme-text-primary));
    }

    &--disabled {
      cursor: default;
      opacity: 0.6;

      &:hover {
        --icon-bg-color: transparent;
      }
    }
  }
}

/* stylelint-enable */
</style>
